/**
 * A2A English Service
 * 
 * Service wrapper for A2A protocol communication with the A2AEnglishAI backend.
 * Provides organized methods for all English learning features:
 * - ESS Toolbox (Five Major Sentence Patterns)
 * - Knowledge Graph analysis
 * - HKDSE Grammar
 * - Primary Grammar
 * - Vocabulary management
 * - Writing tools
 * - GrammarSpeak features
 * - DeepSeek AI capabilities
 * - UX negotiation
 */

import priEnglishClient from '../a2aClient';

/**
 * A2A English Service Class
 * Wraps all A2A capabilities with clean, organized method names
 */
class A2AEnglishService {
  constructor() {
    this.client = priEnglishClient;
  }

  // ============================================================================
  // ESS Toolbox Methods
  // ============================================================================

  /**
   * Analyze passage for Five Major Sentence Patterns (ESS) using DeepSeek AI
   * @param {string} passage - Text passage to analyze
   * @param {Object} options - Analysis options
   * @param {string} options.title - Passage title (optional)
   * @param {boolean} options.includeVisualization - Include visualization data (default: true)
   * @param {boolean} options.includeQuiz - Include quiz questions (default: true)
   * @param {boolean} options.includeRewrite - Include rewrite exercises (default: true)
   * @returns {Promise<Object>} ESS analysis with sentence patterns, visualization, quiz, and rewrite
   */
  async analyzeESSStructure(passage, options = {}) {
    const {
      title = null,
      includeVisualization = true,
      includeQuiz = true,
      includeRewrite = true
    } = options;

    try {
      const result = await this.client.executeTask('analyze_ess_structure', {
        passage,
        title,
        include_visualization: includeVisualization,
        include_quiz: includeQuiz,
        include_rewrite: includeRewrite
      }, {
        timeout: 60000  // 60 seconds for AI analysis
      });
      
      return result;
    } catch (error) {
      console.error('[A2A] analyzeESSStructure error:', error);
      throw error;
    }
  }

  /**
   * Get ESS Toolbox sample passages
   * @param {Object} params - Query parameters
   * @param {string} params.category - Filter by category (optional)
   * @param {string} params.difficulty - Filter by difficulty (optional)
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @returns {Promise<Object>} List of sample passages
   */
  async getESSSamplePassages(params = {}) {
    const {
      category = null,
      difficulty = null,
      page = 1,
      limit = 20
    } = params;

    const result = await this.client.executeTask('get_ess_sample_passages', {
      category,
      difficulty,
      page,
      limit
    });
    
    return result;
  }

  /**
   * Get all 5 ESS sentence patterns with examples
   * @returns {Promise<Object>} All 5 ESS patterns (S+V, S+V+O, SVOO, SVC, SVOC)
   */
  async getESSSentencePatterns() {
    const result = await this.client.executeTask('get_ess_sentence_patterns', {});
    return result;
  }

  /**
   * Save DeepSeek generated story to ESS Toolbox
   * @param {string} title - Story title
   * @param {string} content - Story content
   * @param {Object} options - Save options
   * @param {string} options.category - Story category (default: 'deepseek_story')
   * @param {Object} options.metadata - Additional metadata (optional)
   * @returns {Promise<Object>} Saved story
   */
  async saveESSStory(title, content, options = {}) {
    const {
      category = 'deepseek_story',
      metadata = null
    } = options;

    const result = await this.client.executeTask('save_ess_story', {
      story_id: options.storyId || null,
      title,
      content,
      category,
      difficulty: options.difficulty || null,
      raw: metadata
    });
    
    return result;
  }

  /**
   * Get DeepSeek generated ESS stories
   * @param {Object} params - Query parameters
   * @param {string} params.category - Filter by category (optional)
   * @param {string} params.difficulty - Filter by difficulty (optional)
   * @param {number} params.limit - Maximum number of stories (default: 100)
   * @returns {Promise<Array>} List of DeepSeek stories
   */
  async getESSDeepSeekStories(params = {}) {
    const {
      category = null,
      difficulty = null,
      limit = 100
    } = params;

    const result = await this.client.executeTask('get_ess_deepseek_stories', {
      category,
      difficulty,
      limit
    });
    
    return result;
  }

  // ============================================================================
  // Knowledge Graph Methods
  // ============================================================================

  /**
   * Get knowledge graph stories
   * @param {Object} params - Query parameters
   * @param {string} params.category - Filter by category (optional)
   * @param {string} params.difficulty - Filter by difficulty (optional)
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @returns {Promise<Object>} List of stories with pagination
   */
  async getKnowledgeGraphStories(params = {}) {
    const {
      category = null,
      difficulty = null,
      page = 1,
      limit = 20
    } = params;

    const result = await this.client.executeTask('get_kg_stories', {
      category,
      difficulty,
      page,
      limit
    });
    
    return result;
  }

  /**
   * Get specific knowledge graph story by ID
   * @param {string} storyId - Story ID
   * @returns {Promise<Object>} Story with entities, relationships, and analysis
   */
  async getKnowledgeGraphStory(storyId) {
    const result = await this.client.executeTask('get_kg_story', {
      story_id: storyId
    });
    
    return result;
  }

  /**
   * Analyze text and generate knowledge graph using DeepSeek AI
   * @param {string} text - Text to analyze
   * @param {Object} options - Analysis options
   * @param {string} options.title - Analysis title (optional)
   * @param {boolean} options.extractEntities - Extract entities (default: true)
   * @param {boolean} options.extractRelations - Extract relationships (default: true)
   * @returns {Promise<Object>} Knowledge graph with entities, relationships, themes, and insights
   */
  async analyzeKnowledgeGraph(text, options = {}) {
    const {
      title = null,
      extractEntities = true,
      extractRelations = true
    } = options;

    try {
      // Log current server URL for debugging
      console.log('[A2A] analyzeKnowledgeGraph - Current server URL:', this.client.remoteUrl);
      
      const result = await this.client.executeTask('analyze_knowledge_graph', {
        text,
        title,
        extract_entities: extractEntities,
        extract_relations: extractRelations
      }, {
        timeout: 60000  // 60 seconds for AI analysis
      });
      
      console.log('[A2A] analyzeKnowledgeGraph - Response received:', {
        hasResult: !!result,
        success: result?.success,
        hasEntities: !!(result?.entities),
        entitiesCount: result?.entities?.length || 0
      });
      
      return result;
    } catch (error) {
      console.error('[A2A] analyzeKnowledgeGraph error:', {
        message: error.message,
        name: error.name,
        serverUrl: this.client.remoteUrl,
        stack: error.stack
      });
      throw error;
    }
  }

  // ============================================================================
  // HKDSE Grammar Methods
  // ============================================================================

  /**
   * Get HKDSE grammar categories
   * @returns {Promise<Object>} Grammar categories (parts-of-speech, verb-tenses, etc.)
   */
  async getGrammarCategories() {
    const result = await this.client.executeTask('get_grammar_categories', {});
    return result;
  }

  /**
   * Get parts of speech data (nouns, verbs, adjectives, etc.)
   * @returns {Promise<Object>} Parts of speech with definitions, examples, and rules
   */
  async getPartsOfSpeech() {
    const result = await this.client.executeTask('get_parts_of_speech', {});
    return result;
  }

  /**
   * Get verb tenses data (12 tenses)
   * @returns {Promise<Object>} Verb tenses with timeline, structures, examples, and usage
   */
  async getVerbTenses() {
    const result = await this.client.executeTask('get_verb_tenses', {});
    return result;
  }

  /**
   * Get sentence structure patterns
   * @returns {Promise<Object>} Sentence structures (simple, compound, complex)
   */
  async getSentenceStructure() {
    const result = await this.client.executeTask('get_sentence_structure', {});
    return result;
  }

  /**
   * Get clauses and phrases data
   * @returns {Promise<Object>} Clauses and phrases with definitions, structures, examples
   */
  async getClausesPhrases() {
    const result = await this.client.executeTask('get_clauses_phrases', {});
    return result;
  }

  /**
   * Get common grammar errors
   * @returns {Promise<Object>} Common errors with rules, mistakes, and corrections
   */
  async getCommonErrors() {
    const result = await this.client.executeTask('get_common_errors', {});
    return result;
  }

  /**
   * Get practice exercises
   * @returns {Promise<Object>} Practice exercises with questions and answers
   */
  async getPracticeExercises() {
    const result = await this.client.executeTask('get_practice_exercises', {});
    return result;
  }

  /**
   * Get HKDSE exam tips
   * @returns {Promise<Object>} Exam tips and strategies
   */
  async getExamTips() {
    const result = await this.client.executeTask('get_exam_tips', {});
    return result;
  }

  // ============================================================================
  // Linguistic Analysis Methods
  // ============================================================================

  /**
   * Get linguistic analysis examples
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @returns {Promise<Object>} List of linguistic analysis examples
   */
  async getLinguisticExamples(params = {}) {
    const {
      page = 1,
      limit = 20
    } = params;

    try {
      const result = await this.client.executeTask('get_linguistic_examples', {
        page,
        limit
      }, {
        timeout: 30000  // 30 seconds timeout
      });
      
      // Log response for debugging
      if (process.env.NODE_ENV === 'development') {
        console.log('[A2A] getLinguisticExamples response:', {
          hasResult: !!result,
          resultType: typeof result,
          resultKeys: result ? Object.keys(result) : [],
          hasData: !!(result && result.data),
          hasExamples: !!(result && result.examples),
          isArray: Array.isArray(result)
        });
      }
      
      return result;
    } catch (error) {
      console.error('[A2A] getLinguisticExamples error:', {
        message: error.message,
        name: error.name,
        code: error.code,
        stack: error.stack
      });
      throw error;
    }
  }

  /**
   * Get specific linguistic analysis example
   * @param {string} exampleId - Example ID
   * @returns {Promise<Object>} Linguistic analysis example with detailed analysis
   */
  async getLinguisticExample(exampleId) {
    try {
      const result = await this.client.executeTask('get_linguistic_example', {
        example_id: exampleId
      }, {
        timeout: 30000  // 30 seconds timeout
      });
      
      return result;
    } catch (error) {
      console.error('[A2A] getLinguisticExample error:', {
        message: error.message,
        name: error.name,
        code: error.code,
        exampleId
      });
      throw error;
    }
  }

  // ============================================================================
  // Primary Grammar Methods
  // ============================================================================

  /**
   * Get primary grammar data for all grades (P1-P6)
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 50)
   * @returns {Promise<Object>} Grammar data for all grades
   */
  async getPrimaryGrammar(params = {}) {
    const {
      page = 1,
      limit = 50
    } = params;

    const result = await this.client.executeTask('get_primary_grammar', {
      page,
      limit
    });
    
    return result;
  }

  /**
   * Get primary grammar by specific grade (p1-p6)
   * @param {string} grade - Grade level: "p1", "p2", "p3", "p4", "p5", or "p6"
   * @returns {Promise<Object>} Grammar data for the specified grade
   */
  async getPrimaryGrammarByGrade(grade) {
    const result = await this.client.executeTask('get_primary_grammar_by_grade', {
      grade
    });
    
    return result;
  }

  // ============================================================================
  // Vocabulary Methods
  // ============================================================================

  /**
   * Get vocabulary sets (195 sets with 1,872 words total)
   * @param {Object} params - Query parameters
   * @param {string} params.category - Filter by category (optional)
   * @param {string} params.difficulty - Filter by difficulty level (optional)
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @returns {Promise<Object>} List of vocabulary sets with pagination
   */
  async getVocabularySets(params = {}) {
    const {
      category = null,
      difficulty = null,
      page = 1,
      limit = 20
    } = params;

    const result = await this.client.executeTask('get_vocab_sets', {
      category,
      difficulty,
      page,
      limit
    });
    
    return result;
  }

  /**
   * Get specific vocabulary set with all words
   * @param {string} setId - Vocabulary set ID
   * @returns {Promise<Object>} Vocabulary set with words array
   */
  async getVocabularySet(setId) {
    const result = await this.client.executeTask('get_vocab_set', {
      set_id: setId
    });
    
    return result;
  }

  /**
   * Update vocabulary learning progress
   * @param {string} userId - User ID
   * @param {string} setId - Vocabulary set ID
   * @param {string} wordId - Word ID
   * @param {string} status - Progress status: "learning", "reviewing", or "mastered"
   * @param {Object} options - Additional options
   * @param {number} options.score - Score (0-100, optional)
   * @param {string} options.mode - Learning mode: "read", "study", or "test" (default: "study")
   * @returns {Promise<Object>} Updated progress
   */
  async updateVocabProgress(userId, setId, wordId, status, options = {}) {
    const {
      score = null,
      mode = 'study'
    } = options;

    const result = await this.client.executeTask('update_vocab_progress', {
      user_id: userId,
      set_id: setId,
      word_id: wordId,
      status,
      score,
      mode
    });
    
    return result;
  }

  /**
   * Get vocabulary learning calendar
   * @param {string} userId - User ID
   * @param {number} year - Year (e.g., 2025)
   * @param {number} month - Month (1-12)
   * @returns {Promise<Object>} Calendar data with daily progress
   */
  async getVocabCalendar(userId, year, month) {
    const result = await this.client.executeTask('get_vocab_calendar', {
      user_id: userId,
      year,
      month
    });
    
    return result;
  }

  // ============================================================================
  // Writing Tools Methods
  // ============================================================================

  /**
   * Get sentence expansion examples
   * @param {Object} params - Query parameters
   * @param {string} params.category - Filter by category (Nature, Animals, Objects, Emotions, optional)
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @returns {Promise<Object>} List of expansion examples
   */
  async getWritingExamples(params = {}) {
    const {
      category = null,
      page = 1,
      limit = 20
    } = params;

    const result = await this.client.executeTask('get_writing_examples', {
      category,
      page,
      limit
    });
    
    return result;
  }

  /**
   * Save writing composition
   * @param {Object} compositionData - Composition data
   * @param {string} compositionData.userId - User ID
   * @param {string} compositionData.title - Composition title
   * @param {string} compositionData.content - Composition content/text
   * @param {string} compositionData.style - Composition style (default: "composition")
   * @param {number} compositionData.wordLength - Word count (default: 0)
   * @param {Array} compositionData.nodesData - Node-based composition data (optional)
   * @param {string} compositionData.category - Composition category (default: "AIComposition")
   * @returns {Promise<Object>} Saved composition
   */
  async saveWritingComposition(compositionData) {
    const {
      userId,
      title,
      content,
      style = 'composition',
      wordLength = 0,
      nodesData = null,
      category = 'AIComposition'
    } = compositionData;

    const result = await this.client.executeTask('save_writing_composition', {
      user_id: userId,
      title,
      content,
      style,
      word_length: wordLength,
      nodes_data: nodesData,
      category
    });
    
    return result;
  }

  // ============================================================================
  // GrammarSpeak Methods
  // ============================================================================

  /**
   * Get grammar topics (90+ structures)
   * @param {Object} params - Query parameters
   * @param {string} params.category - Filter by category (optional)
   * @param {string} params.level - Filter by level (beginner, intermediate, advanced, optional)
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @returns {Promise<Object>} List of grammar topics
   */
  async getGrammarTopics(params = {}) {
    const {
      category = null,
      level = null,
      page = 1,
      limit = 20
    } = params;

    const result = await this.client.executeTask('get_grammar_topics', {
      category,
      level,
      page,
      limit
    });
    
    return result;
  }

  /**
   * Get Grammar Book units (13 units)
   * @param {Object} params - Query parameters
   * @param {string} params.bookId - Filter by book ID (optional)
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @returns {Promise<Object>} List of grammar units
   */
  async getGrammarUnits(params = {}) {
    const {
      bookId = null,
      page = 1,
      limit = 20
    } = params;

    const result = await this.client.executeTask('get_grammar_units', {
      book_id: bookId,
      page,
      limit
    });
    
    return result;
  }

  /**
   * Get learning paths
   * @returns {Promise<Object>} List of learning paths
   */
  async getLearningPaths() {
    const result = await this.client.executeTask('get_learning_paths', {});
    return result;
  }

  /**
   * Get roleplay scenarios for Game Mode Courses
   * @param {Object} params - Query parameters
   * @param {string} params.category - Filter by category (optional)
   * @param {string} params.difficulty - Filter by difficulty (optional)
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @returns {Promise<Object>} List of roleplay scenarios
   */
  async getRoleplayScenarios(params = {}) {
    const {
      category = null,
      difficulty = null,
      page = 1,
      limit = 20
    } = params;

    const result = await this.client.executeTask('get_roleplay_scenarios', {
      category,
      difficulty,
      page,
      limit
    });
    
    return result;
  }

  /**
   * Get daily grammar challenges
   * @param {Object} params - Query parameters
   * @param {string} params.date - Date in YYYY-MM-DD format (default: today)
   * @returns {Promise<Object>} List of daily challenges with user progress
   */
  async getDailyChallenges(params = {}) {
    const {
      date = null
    } = params;

    const result = await this.client.executeTask('get_daily_challenges', {
      date
    });
    
    return result;
  }

  /**
   * Complete daily challenge
   * @param {string} userId - User ID
   * @param {string} challengeId - Challenge ID
   * @param {number} score - Score (0-100)
   * @param {number} timeSpent - Time spent in seconds
   * @returns {Promise<Object>} Completion result with XP earned
   */
  async completeChallenge(userId, challengeId, score, timeSpent) {
    const result = await this.client.executeTask('complete_challenge', {
      user_id: userId,
      challenge_id: challengeId,
      score,
      time_spent: timeSpent
    });
    
    return result;
  }

  /**
   * Chat with AI Learning Assistant
   * @param {string} userId - User ID
   * @param {string} message - User message
   * @param {Object} options - Chat options
   * @param {Object} options.context - Conversation context (optional)
   * @returns {Promise<Object>} AI assistant response
   */
  async chatAIAssistant(userId, message, options = {}) {
    const {
      context = null
    } = options;

    try {
      const result = await this.client.executeTask('chat_ai_assistant', {
        user_id: userId,
        message,
        context
      }, {
        timeout: 60000  // 60 seconds for AI chat
      });
      
      return result;
    } catch (error) {
      console.error('[A2A] chatAIAssistant error:', error);
      throw error;
    }
  }

  // ============================================================================
  // DeepSeek AI Methods
  // ============================================================================

  /**
   * DeepSeek AI chat completion (centralized proxy)
   * @param {string} message - User message
   * @param {Object} options - Chat options
   * @param {Object} options.context - Conversation context (optional)
   * @param {string} options.model - Model name (default: "deepseek-chat")
   * @param {number} options.temperature - Temperature (0-1, default: 0.7)
   * @param {number} options.maxTokens - Maximum tokens (default: 2000)
   * @returns {Promise<Object>} AI response with usage and cost
   */
  async deepseekChat(message, options = {}) {
    const {
      context = null,
      model = 'deepseek-chat',
      temperature = 0.7,
      maxTokens = 2000
    } = options;

    try {
      const result = await this.client.executeTask('deepseek_chat', {
        message,
        context,
        model,
        temperature,
        max_tokens: maxTokens
      }, {
        timeout: 60000  // 60 seconds for AI chat
      });
      
      return result;
    } catch (error) {
      console.error('[A2A] deepseekChat error:', error);
      throw error;
    }
  }

  /**
   * Generate English drill questions using DeepSeek AI
   * @param {Object} drillConfig - Drill configuration
   * @param {string} drillConfig.originalQuestion - Original question to base drills on
   * @param {string} drillConfig.questionType - Question type: "grammar", "vocabulary", "comprehension" (default: "grammar")
   * @param {string} drillConfig.difficulty - Difficulty level (default: "intermediate")
   * @param {string} drillConfig.grade - Grade level (e.g., "p1", "p2", "p3", "p4", "p5", "p6", optional)
   * @param {number} drillConfig.numberOfDrills - Number of drill questions to generate (default: 3)
   * @returns {Promise<Object>} Generated drill questions with answers and explanations
   */
  async generateDrillQuestions(drillConfig) {
    const {
      originalQuestion,
      questionType = 'grammar',
      difficulty = 'intermediate',
      grade = null,
      numberOfDrills = 3
    } = drillConfig;

    try {
      const result = await this.client.executeTask('generate_drill_questions', {
        original_question: originalQuestion,
        question_type: questionType,
        difficulty,
        grade,
        number_of_drills: numberOfDrills
      }, {
        timeout: 60000  // 60 seconds for AI generation
      });
      
      return result;
    } catch (error) {
      console.error('[A2A] generateDrillQuestions error:', error);
      throw error;
    }
  }

  /**
   * Expand English sentence with context and techniques
   * @param {string} keyword - Base keyword to expand
   * @param {Object} options - Expansion options
   * @param {string} options.context - Context for expansion (optional)
   * @param {string} options.expansionType - Expansion type: "descriptive", "narrative", "emotional" (default: "descriptive")
   * @param {Array} options.techniques - Writing techniques to use (optional)
   * @returns {Promise<Object>} Expanded sentences with steps
   */
  async expandSentence(keyword, options = {}) {
    const {
      context = null,
      expansionType = 'descriptive',
      techniques = null
    } = options;

    try {
      const result = await this.client.executeTask('expand_english_sentence', {
        keyword,
        context,
        expansion_type: expansionType,
        techniques
      }, {
        timeout: 60000  // 60 seconds for AI expansion
      });
      
      return result;
    } catch (error) {
      console.error('[A2A] expandSentence error:', error);
      throw error;
    }
  }

  // ============================================================================
  // UX Negotiation Methods
  // ============================================================================

  /**
   * Negotiate optimal UX format based on user context
   * @param {Object} context - User context (optional, auto-detected if not provided)
   * @param {string} context.device - Device type ('mobile', 'tablet', 'desktop')
   * @param {number} context.screenWidth - Screen width in pixels
   * @param {number} context.screenHeight - Screen height in pixels
   * @param {string} context.bandwidth - Network bandwidth ('2g', '3g', '4g', 'wifi')
   * @param {string} context.userLevel - User level ('beginner', 'intermediate', 'advanced')
   * @param {string} context.appName - Application name (EnglishDaily, PriEnglishDaily, etc.)
   * @returns {Promise<Object>} UX format recommendations
   */
  async negotiateUX(context = null) {
    // Auto-detect context if not provided
    const userContext = context || this.detectContext();
    
    return await this.client.negotiateUX(userContext);
  }

  /**
   * Detect current user context
   * @returns {Object} Detected user context
   */
  detectContext() {
    return this.client.detectContext();
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /**
   * Get all available capabilities
   * @returns {Array|null} Array of capabilities
   */
  getCapabilities() {
    return this.client.getCapabilities();
  }

  /**
   * Check if a specific capability is available
   * @param {string} capabilityName - Capability name
   * @returns {Promise<boolean>} True if capability exists
   */
  async hasCapability(capabilityName) {
    return await this.client.hasCapability(capabilityName);
  }

  /**
   * Get rate limit information
   * @returns {Object} Rate limit info with remaining requests and reset time
   */
  getRateLimitInfo() {
    return this.client.getRateLimitInfo();
  }

  /**
   * Clear cached capabilities (force refresh)
   */
  clearCache() {
    this.client.clearCache();
  }

  /**
   * Cleanup - unsubscribe from all tasks
   */
  cleanup() {
    this.client.cleanup();
  }
}

/**
 * Create and export singleton instance
 */
const a2aEnglishService = new A2AEnglishService();

export default a2aEnglishService;

/**
 * Export class for testing or custom instances
 */
export { A2AEnglishService };

